import { z } from 'zod';
import { EntityId } from './ids';

export const CaptureKind = z.enum([
  'photo',
  'video',
  'voice',
  'note',
  'gps',
  'barcode',
  /**
   * A drawn signature, stored as a PNG like any other media.
   *
   * Its own kind rather than a `photo`, because `kind` is what a reviewer
   * reads to know what they are looking at and a signature is not a
   * photograph of anything. It is also what keeps it out of
   * `AI_EXTRACTABLE_KINDS`: sending someone's signature to a model to have
   * form fields read off it is the one thing this capture must never do.
   *
   * Distinct from `signature.upload`, which is the session-level sign-off at
   * the end of a submission. This one answers a `signature` form *field*.
   */
  'signature',
]);
export type CaptureKind = z.infer<typeof CaptureKind>;

export const GeoPoint = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyMeters: z.number().nonnegative().nullish(),
  altitudeMeters: z.number().nullish(),
  capturedAt: z.string().datetime(),
});
export type GeoPoint = z.infer<typeof GeoPoint>;

export const CaptureAnnotation = z.object({
  id: z.string().uuid(),
  kind: z.enum(['circle', 'arrow', 'label', 'freehand']),
  label: z.string().max(120).optional(),
  // normalised [0,1] coordinates so annotations survive resizing
  points: z.array(z.tuple([z.number(), z.number()])),
  color: z.string().regex(/^#[0-9a-fA-F]{3,8}$/),
});
export type CaptureAnnotation = z.infer<typeof CaptureAnnotation>;

export const CaptureBase = z.object({
  id: EntityId,
  sessionId: EntityId,
  kind: CaptureKind,
  /** OPFS path to the binary blob — null for text-only captures like notes. */
  blobPath: z.string().nullable(),
  /** Filesize of the blob in bytes; 0 for note/gps. */
  byteSize: z.number().int().nonnegative(),
  gps: GeoPoint.nullable(),
  tags: z.array(z.string()).default([]),
  /**
   * When the device says this was taken.
   *
   * Distinct from `createdAt`, which on a row that arrived by sync is the
   * moment the *server* first saw it. On an offline-first product those are
   * routinely days apart, so a record that reports only the second one answers
   * "when was this taken" with "when did the radio come back".
   *
   * A claim rather than a measurement — the inspector controls the device
   * clock — which is exactly why both are kept. The server holds its own
   * arrival time as an honest upper bound, and the gap between the two is how
   * long the work sat unsynced.
   *
   * Null means no device ever said: a capture recorded before this field
   * existed. Never derived from `createdAt` to fill the gap; inventing a
   * capture time is the one thing this field must not do.
   */
  capturedAt: z.string().datetime().nullable().default(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Derived/AI fields, confidence in [0,1]. */
  aiFields: z
    .record(z.object({ value: z.string(), confidence: z.number().min(0).max(1) }))
    .default({}),
  /**
   * What happened when this capture was sent for extraction, and why.
   *
   * `''` — no run recorded yet: either the worker has not reached it or the
   * capture has never been synced. `'succeeded'` with an empty `aiFields` is
   * the interesting one: the model read this and reported nothing, which the
   * prompts ask for explicitly rather than treating as failure. `'skipped'`
   * and `'failed'` carry their reason in `aiDetail`.
   *
   * It exists because all four used to look the same on the phone — a field
   * with no suggestion — and they call for different next actions. Server-
   * owned, like `aiFields`: written by the extraction worker, never sent up.
   */
  aiStatus: z.string().default(''),
  aiDetail: z.string().default(''),
  /** Used to link a capture to a specific form field (traceability). */
  linkedFieldId: z.string().nullable().default(null),
  /**
   * The code that was in context when this was captured — the last thing
   * scanned or typed as a subject. Null for site-level evidence.
   *
   * A string, deliberately, and never a foreign key. It records what the
   * inspector was standing in front of; it does not assert that the thing
   * exists, has properties, or persists between sessions. That distinction is
   * the whole boundary between this product and an asset register, and it is
   * the same standing `gps` already has: a coordinate stamped on an
   * observation, with no table of places behind it.
   *
   * It exists because one session covers many things. A school fire-door
   * survey walks forty doors, and `field_values` is keyed
   * `(session_id, field_id)` — the form can hold exactly one door id. So the
   * form is the header and the captures are the body, grouped by this.
   */
  subjectRef: z.string().nullable().default(null),
});

export const PhotoCapture = CaptureBase.extend({
  kind: z.literal('photo'),
  annotations: z.array(CaptureAnnotation).default([]),
  width: z.number().int().positive().nullish(),
  height: z.number().int().positive().nullish(),
});

export const VideoCapture = CaptureBase.extend({
  kind: z.literal('video'),
  /**
   * Recorded length, hard-capped at ten seconds by both recorders.
   *
   * Required, not optional: it is the number that proves the cap held, and
   * both the timeline badge and the review scrubber read it. Treat it as an
   * upper bound rather than a measurement — it comes off a wall clock, and a
   * recording truncated by `maxFileSize` finishes early without telling the
   * ticker. The review screen reconciles it against the player.
   */
  durationMs: z.number().int().nonnegative(),
  width: z.number().int().positive().nullish(),
  height: z.number().int().positive().nullish(),
  /**
   * Device-local path to a JPEG grabbed at t=0, for the timeline thumbnail.
   *
   * Never uploaded. Two files under one capture id cost nothing — the media
   * store keys on `{sessionId, captureId, extension}` — and the server side
   * gets its poster free from `<video preload="metadata">` once Range works,
   * so there is no second media row to reconcile.
   */
  posterPath: z.string().nullable().default(null),
  /** Narration, once anything writes it. Nothing does yet. */
  transcript: z.string().nullable().default(null),
});

export const VoiceCapture = CaptureBase.extend({
  kind: z.literal('voice'),
  durationMs: z.number().int().nonnegative(),
  transcript: z.string().nullable().default(null),
});

export const NoteCapture = CaptureBase.extend({
  kind: z.literal('note'),
  title: z.string().max(200).default(''),
  body: z.string().default(''),
});

export const GpsCapture = CaptureBase.extend({
  kind: z.literal('gps'),
});

export const SignatureCapture = CaptureBase.extend({
  kind: z.literal('signature'),
  /** Who signed, as typed by whoever collected it. */
  signerName: z.string().max(200).nullable().default(null),
});

export const BarcodeCapture = CaptureBase.extend({
  kind: z.literal('barcode'),
  value: z.string(),
  /**
   * The symbology the value was decoded from — `code_128`, `qr_code` — or null
   * when it was typed rather than scanned.
   *
   * Null rather than a `'manual'` member, because this field answers "which
   * barcode standard produced these characters" and hand-typing is not one.
   * Writing `'manual'` here put a provenance claim in a format field, where
   * the review screen renders it as a symbology and the suggestion rationale
   * read "Scanned manual". Absence is the honest answer, and the reader that
   * cares says "typed".
   */
  format: z.string().nullable().default(null),
});

export const Capture = z.discriminatedUnion('kind', [
  PhotoCapture,
  VideoCapture,
  VoiceCapture,
  NoteCapture,
  GpsCapture,
  BarcodeCapture,
  SignatureCapture,
]);
export type Capture = z.infer<typeof Capture>;
